// frontend/src/components/common/KeyboardShortcutHint.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { Keyboard as KeyboardIcon } from '@mui/icons-material';

const Kbd = ({ children }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-block',
      px: 0.75,
      py: 0.25,
      mx: 0.25,
      fontSize: '0.75rem',
      fontFamily: 'monospace',
      color: 'text.primary',
      bgcolor: 'grey.100',
      border: '1px solid',
      borderColor: 'grey.300',
      borderRadius: 0.5,
      boxShadow: '0 1px 0 rgba(0,0,0,0.1)',
    }}
  >
    {children}
  </Box>
);

const ShortcutRow = ({ keys, description }) => (
  <Box display="flex" justifyContent="space-between" alignItems="center" py={0.75}>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
    <Box sx={{ whiteSpace: 'nowrap', ml: 2 }}>
      {keys.map((key, i) => (
        <React.Fragment key={i}>
          {i > 0 && <Typography component="span" variant="caption" sx={{ mx: 0.25 }}>+</Typography>}
          <Kbd>{key}</Kbd>
        </React.Fragment>
      ))}
    </Box>
  </Box>
);

/**
 * Global keyboard shortcut help dialog.
 * Shows application-wide navigation shortcuts.
 * Can be opened with Alt+K from anywhere.
 */
export default function KeyboardShortcutHint({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <KeyboardIcon />
        Keyboard Shortcuts
      </DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Global Navigation
        </Typography>
        <ShortcutRow keys={['Alt', 'K']} description="Open this shortcut help" />
        <ShortcutRow keys={['Alt', 'D']} description="Go to Dashboard" />
        <ShortcutRow keys={['Alt', 'P']} description="Go to Projects" />
        <ShortcutRow keys={['Alt', 'U']} description="Go to Users" />
        <ShortcutRow keys={['Alt', 'A']} description="Go to Analytics" />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="primary" gutterBottom>
          Projects List
        </Typography>
        <ShortcutRow keys={['Alt', 'N']} description="Create new project" />

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" color="primary" gutterBottom>
          Translation Review (when in review page)
        </Typography>
        <ShortcutRow keys={['Tab']} description="Next unreviewed segment" />
        <ShortcutRow keys={['Shift', 'Tab']} description="Previous segment" />
        <ShortcutRow keys={['Ctrl', 'Enter']} description="Save and move to next" />
        <ShortcutRow keys={['Shift', 'Enter']} description="Save without moving" />
        <ShortcutRow keys={['Ctrl', 'Shift', 'S']} description="Copy source to target" />
        <ShortcutRow keys={['Esc']} description="Cancel editing" />
        <ShortcutRow keys={['Alt', 'C']} description="Open Comments panel for current segment" />
        <ShortcutRow keys={['F3']} description="Concordance search (with current selection)" />
        <ShortcutRow keys={['F9']} description="Insert next unfulfilled glossary term at caret" />
        <ShortcutRow keys={['Ctrl', 'Z']} description="Undo last segment save (target text)" />
        <ShortcutRow keys={['Ctrl', 'Shift', 'Z']} description="Redo last undone save" />
        <ShortcutRow keys={['Ctrl', 'B']} description="Take a break (pause tracking)" />

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.disabled">
          Press <strong>?</strong> or <strong>Alt+K</strong> anywhere to open this dialog.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Small floating button to open the keyboard shortcuts dialog.
 */
export function KeyboardShortcutButton() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const isAltK = e.altKey && (e.code === 'KeyK' || String(e.key).toLowerCase() === 'k');
      // `?` on US layouts is Shift+/. Some layouts emit it directly as e.key === '?'.
      // Skip when typing in a text input so users can literally type `?`.
      const inTextField = ['INPUT', 'TEXTAREA'].includes(e.target?.tagName)
        || e.target?.isContentEditable;
      const isQuestion = !inTextField && (e.key === '?' || (e.shiftKey && e.key === '/'));
      if (isAltK || isQuestion) {
        e.preventDefault();
        setOpen(true);
      }
    };
    // Capture phase so TextField / MUI InputBase can't swallow the shortcut.
    window.addEventListener('keydown', handleKeyDown, true);
    // Custom-event bridge so any component (e.g. a page's toolbar button) can
    // open this dialog without cross-tree state plumbing.
    const openViaEvent = () => setOpen(true);
    window.addEventListener('mtx:open-shortcuts', openViaEvent);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('mtx:open-shortcuts', openViaEvent);
    };
  }, []);

  return (
    <>
      <Tooltip title="Keyboard Shortcuts (Alt+K)">
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          aria-label="Show keyboard shortcuts"
          sx={{
            position: 'fixed',
            bottom: 16,
            left: 16,
            zIndex: 1200,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 1,
            '&:hover': { bgcolor: 'grey.100' }
          }}
        >
          <KeyboardIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <KeyboardShortcutHint open={open} onClose={() => setOpen(false)} />
    </>
  );
}
